"use client";

import { api } from "../../convex/_generated/api";
import { Fragment, useState, useMemo, useRef, useEffect } from "react";
import { Popover, Transition } from "@headlessui/react";
import { Check, ChevronDown, ChevronRight, Globe, MapPin, X } from "lucide-react";
import { Id } from "../../convex/_generated/dataModel";
import { useQuery } from 'convex/react';
import { useTranslations } from 'next-intl';
import { getFlagEmoji } from "@/lib/utils";

interface LocationSelectorProps {
  value?: Id<"locations"> | Id<"locations">[];
  onChange: (value: Id<"locations"> | Id<"locations">[]) => void;
  selectedRegionId?: Id<"regions">;
  onRegionChange?: (id: Id<"regions"> | undefined) => void;
  disabled?: boolean;
  multi?: boolean;
  type?: "country" | "state" | "city"; // For backwards compatibility
  size?: "small" | "large";
  align?: "left" | "right";
  placeholder?: string;
  limitedToOrganization?: boolean;
  allowedLocationIds?: Id<"locations">[];
  allowClear?: boolean;
}

interface AlignmentGuardProps {
  open: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  setAutoAlign: (align: "left" | "right") => void;
  preferred: "left" | "right";
}

function AlignmentGuard({ open, containerRef, setAutoAlign, preferred }: AlignmentGuardProps) {
  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const panelWidth = 560;
      
      const spaceRight = windowWidth - rect.left;
      const spaceLeft = rect.right;
      
      if (preferred === "left") {
        if (spaceRight < panelWidth && spaceLeft > spaceRight) {
          setAutoAlign("right");
        } else {
          setAutoAlign("left");
        }
      } else {
        if (spaceLeft < panelWidth && spaceRight > spaceLeft) {
          setAutoAlign("left");
        } else {
          setAutoAlign("right");
        }
      }
    }
  }, [open, containerRef, setAutoAlign, preferred]);
  
  return null;
}

export default function LocationSelector({
  value,
  onChange,
  selectedRegionId,
  onRegionChange,
  disabled = false,
  multi = false,
  type = "country",
  size = "small",
  placeholder,
  align,
  limitedToOrganization,
  allowedLocationIds,
  allowClear = false,
}: LocationSelectorProps) {
  const t = useTranslations('shared.locationSelector');
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoAlign, setAutoAlign] = useState<"left" | "right">("right");
  const organizations = useQuery(api.organizations.getOrganizations)
  const organization = organizations?.[0];
  const regions = useQuery(api.locations.getRegions);
  const allLocations = useQuery(api.locations.getLocations, {});
  const [activeRegionId, setActiveRegionId] = useState<Id<"regions"> | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter locations to only show those that are in the organization/allowed list
  const orgLocations = useMemo(() => {
    if (!allLocations) return [];
    let filtered = allLocations;
    if (limitedToOrganization) {
      const orgSet = new Set(organization?.locationIds as string[]);
      filtered = allLocations.filter(loc => orgSet.has(loc._id));
    } else if (allowedLocationIds) {
      const allowedSet = new Set(allowedLocationIds as string[]);
      filtered = allLocations.filter(loc => allowedSet.has(loc._id));
    } else if (type) {
      filtered = allLocations.filter(l => l.type === type);
    }
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(l => {
        if (l.name.toLowerCase().includes(q)) return true;
        if (l.code?.toLowerCase().includes(q)) return true;
        const initials = l.name.split(/\s+/).map(w => w[0]).join("").toLowerCase();
        return initials.includes(q);
      });
    }
    return filtered;
  }, [allLocations, organization, type, searchQuery]);

  // Filter regions to only show those that have locations in the organization
  const relevantRegions = useMemo(() => {
    if (!regions || orgLocations.length === 0) return [];
    const relevantRegionIds = new Set(orgLocations.map(loc => loc.regionId).filter(Boolean));
    return regions.filter(region => relevantRegionIds.has(region._id));
  }, [regions, orgLocations]);

  // Map locations by region
  const locationsByRegion = useMemo(() => {
    const map = new Map<Id<"regions">, any[]>();
    orgLocations.forEach(loc => {
      if (loc.regionId) {
        if (!map.has(loc.regionId)) map.set(loc.regionId, []);
        map.get(loc.regionId)!.push(loc);
      }
    });
    return map;
  }, [orgLocations]);

  const selectedLocationIds = useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  const regionSelectionStats = useMemo(() => {
    const stats = new Map<Id<"regions">, { selected: number; total: number }>();
    if (!relevantRegions || !locationsByRegion) return stats;
    
    relevantRegions.forEach(region => {
      const regionLocs = locationsByRegion.get(region._id) || [];
      const selectedCount = regionLocs.filter(l => selectedLocationIds.includes(l._id)).length;
      stats.set(region._id, { selected: selectedCount, total: regionLocs.length });
    });
    return stats;
  }, [relevantRegions, locationsByRegion, selectedLocationIds]);

  const handleSelectAll = (close: () => void) => {
    onChange([]);
    if (onRegionChange) onRegionChange(undefined);
    setActiveRegionId(null);
    close();
  };

  const handleRegionClick = (regionId: Id<"regions">, close: () => void) => {
    const regionLocations = locationsByRegion.get(regionId) || [];
    const regionLocIds = regionLocations.map(l => l._id);
    
    if (multi) {
      const allSelected = regionLocIds.every(id => selectedLocationIds.includes(id));
      if (allSelected) {
        // If all selected, clear them
        const newSelected = selectedLocationIds.filter(id => !regionLocIds.includes(id));
        onChange(newSelected);
        if (onRegionChange) onRegionChange(undefined);
      } else {
        // If not all selected, select them all (merging with existing selection)
        const newSelected = Array.from(new Set([...selectedLocationIds, ...regionLocIds]));
        onChange(newSelected);
        if (newSelected.length === regionLocIds.length) {
          if (onRegionChange) onRegionChange(regionId);
        } else {
          if (onRegionChange) onRegionChange(undefined);
        }
      }
    } else {
      onChange(regionLocIds[0]);
      if (onRegionChange) onRegionChange(regionId);
      close();
    }
  };

  const handleLocationToggle = (locationId: Id<"locations">, regionId: Id<"regions"> | undefined, close: () => void) => {
    if (multi) {
      const newSelected = [...selectedLocationIds];
      const index = newSelected.indexOf(locationId);
      
      if (index > -1) {
        newSelected.splice(index, 1);
      } else {
        newSelected.push(locationId);
      }
      
      onChange(newSelected);
      
      if (regionId) {
        const regionLocations = locationsByRegion.get(regionId) || [];
        const allInRegionSelected = regionLocations.every(l => newSelected.includes(l._id));
        const onlyRegionSelected = newSelected.length === regionLocations.length && allInRegionSelected;

        if (onRegionChange) {
          if (onlyRegionSelected) {
            onRegionChange(regionId);
          } else {
            onRegionChange(undefined);
          }
        }
      } else if (onRegionChange) {
        onRegionChange(undefined);
      }
    } else {
      onChange(locationId);
      if (onRegionChange) onRegionChange(undefined);
      close();
    }
  };


  const isAllSelected = selectedLocationIds.length === 0 && multi;

  const getLabel = () => {
    if (selectedLocationIds.length === 0 && placeholder) return placeholder;
    if (isAllSelected || (!multi && selectedLocationIds.length === 0)) return t('allLocations');
    if (selectedRegionId) {
      const region = relevantRegions.find(r => r._id === selectedRegionId);
      const regionLocs = locationsByRegion.get(selectedRegionId) || [];
      if (selectedLocationIds.length === regionLocs.length) {
        return t('regionNameAll', { regionName: region?.name ?? '' });
      }
      return t('regionNameCount', { regionName: region?.name ?? '', selected: selectedLocationIds.length, total: regionLocs.length });
    }
    if (selectedLocationIds.length === 1) {
      const loc = orgLocations.find(l => l._id === selectedLocationIds[0]);
      return loc?.name || t('oneLocation');
    }
    return t('multipleLocations', { count: selectedLocationIds.length });
  };

  const preferred = size === "large" ? "left" : "right";
  const effectiveAlign = align || autoAlign;

  return (
    <div ref={containerRef} className={`relative w-full`}>
      <Popover as="div" className="w-full">
        {({ open, close }) => (
          <>
            <AlignmentGuard
              open={open}
              containerRef={containerRef}
              setAutoAlign={setAutoAlign}
              preferred={preferred}
            />
            {/* Initialize activeRegionId when popover opens */}
            {(() => {
              if (open && !activeRegionId && selectedRegionId) {
                setActiveRegionId(selectedRegionId);
              } else if (open && !activeRegionId && selectedLocationIds.length > 0) {
                const firstLoc = orgLocations.find(l => selectedLocationIds.includes(l._id));
                if (firstLoc?.regionId) setActiveRegionId(firstLoc.regionId);
              } else if (open && !activeRegionId && relevantRegions.length > 0) {
                setActiveRegionId(relevantRegions[0]._id);
              }
              return null;
            })()}
            <Popover.Button
              type="button"
              disabled={disabled}
              className={size === "large" 
                ? `relative flex items-center justify-between w-full cursor-default rounded-lg bg-background text-left border border-input focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 py-3 pl-3 pr-2 hover:border-accent-hover overflow-hidden ${open ? 'ring-2 ring-primary border-primary' : ''}`
                : `glass-card px-4 py-2 rounded-lg flex items-center gap-2 w-full justify-between transition-all duration-200 focus:ring-2 focus:outline-none ${open ? 'ring-2 ring-lime-500/50' : ''}`}
            >
              <div className="flex items-center gap-2">
                  {selectedLocationIds.length === 1 && !selectedRegionId ? (
                    <span className="text-base leading-4">
                      {(() => {
                        const loc = orgLocations.find(l => l._id === selectedLocationIds[0]);
                        return loc ? getFlagEmoji(loc.country || loc.code) : <Globe className={size === "large" ? "h-5 w-5" : "w-4 h-4"} style={{ color: 'var(--color-text-muted)' }} />;
                      })()}
                    </span>
                  ) : (
                    <Globe className={size === "large" ? "h-5 w-5" : "w-4 h-4"} style={{ color: 'var(--color-text-muted)' }} />
                  )}
                <span className={`truncate text-foreground ${size === "large" ? "text-base" : "text-sm max-w-[120px]"}`}>
                  {getLabel()}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                {allowClear && selectedLocationIds.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onChange(multi ? [] : undefined as any);
                      if (onRegionChange) onRegionChange(undefined);
                      setActiveRegionId(null);
                    }}
                    className={`p-0.5 rounded-full hover:bg-muted transition-colors group/clear ${size === "large" ? "mr-0.5" : ""}`}
                    title={t('clearSelection')}
                  >
                    <X className={`${size === "large" ? "h-4 w-4" : "w-3.5 h-3.5"} text-muted-foreground group-hover/clear:text-foreground`} />
                  </button>
                )}
                <ChevronDown className={`${size === "large" ? "h-5 w-5" : "w-4 h-4"} transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--color-text-muted)' }} />
              </div>
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
              afterLeave={() => setSearchQuery("")}
            >
              <Popover.Panel className={`absolute z-50 mt-2 bg-surface rounded-xl shadow-2xl border border-border overflow-visible min-w-full sm:w-[560px] max-w-[calc(100vw-2rem)] ${effectiveAlign === "left" ? 'left-0 origin-top-left' : 'right-0 origin-top-right'}`}>
                <div className="flex flex-col">
                  {/* Search Input */}
                  <div className="p-3 border-b border-border">
                    <div className="relative">
                      <input
                        type="text"
                        autoFocus
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('searchPlaceholder')}
                        className="w-full bg-surface-hover border border-border rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-500/30"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>

                  <div className="flex h-[400px]">
                    {searchQuery ? (
                      <div className="flex-1 py-2 overflow-y-auto px-2">
                        {orgLocations.length > 0 ? (
                          orgLocations.map((loc) => (
                            <button
                              type="button"
                              key={loc._id}
                              onClick={() => handleLocationToggle(loc._id, loc.regionId, close)}
                              className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors rounded-lg hover:bg-surface-hover ${selectedLocationIds.includes(loc._id) ? 'text-lime-400 font-medium bg-lime-500/5' : 'text-foreground'}`}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-base flex-shrink-0">{getFlagEmoji(loc.country || loc.code)}</span>
                                <span className="truncate">{loc.name}</span>
                              </div>
                              {selectedLocationIds.includes(loc._id) && <Check className="w-4 h-4" />}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                            {t('noResults', { query: searchQuery })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Desktop View (Two Columns) — currently disabled in favor of the flat list below */}
                        <div className="hidden w-full h-full">
                          {/* Regions Column */}
                          <div className="w-56 py-2 border-r border-border overflow-y-auto flex-shrink-0">
                            {multi && (
                              <>
                                <div className="px-3 py-1 mb-1">
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground opacity-60">{t('regionsLabel')}</span>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => handleSelectAll(close)}
                                  className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors hover:bg-surface-hover ${isAllSelected ? 'text-lime-400 font-medium bg-lime-500/5' : 'text-foreground'}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <Globe className="w-4 h-4" />
                                    <span>{t('allLocations')}</span>
                                  </div>
                                  {isAllSelected && <Check className="w-4 h-4" />}
                                </button>

                                <div className="h-px bg-border my-2 mx-2" />
                              </>
                            )}

                            {relevantRegions.length > 0 ? (
                              relevantRegions.map((region) => (
                                <button
                                  type="button"
                                  key={region._id}
                                  onMouseEnter={() => setActiveRegionId(region._id)}
                                  onClick={() => handleRegionClick(region._id, close)}
                                  className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors hover:bg-surface-hover group 
                                    ${activeRegionId === region._id ? 'bg-surface-hover' : ''} 
                                    ${(selectedRegionId === region._id || (regionSelectionStats.get(region._id)?.selected ?? 0) > 0) ? 'text-lime-400 font-medium' : 'text-foreground'}`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="truncate">{region.name}</span>
                                    {(() => {
                                      const stats = regionSelectionStats.get(region._id);
                                      if (!stats || stats.selected === 0) return null;
                                      return (
                                        <span className="text-[10px] opacity-70 whitespace-nowrap">
                                          ({stats.selected === stats.total ? t('all') : `${stats.selected}/${stats.total}`})
                                        </span>
                                      );
                                    })()}
                                  </div>
                                  <ChevronRight className={`w-4 h-4 transition-transform opacity-40 group-hover:opacity-100 ${activeRegionId === region._id ? 'translate-x-0.5 opacity-100' : ''}`} />
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                                {t('noRegions')}
                              </div>
                            )}
                          </div>

                          {/* Locations Column */}
                          <div className="flex-1 py-2 overflow-y-auto bg-surface-hover/20">
                            {activeRegionId ? (
                              <>
                                <div className="px-4 py-1 mb-1 flex items-center justify-between">
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground opacity-60">
                                    {t('locationsColumnLabel', { regionName: relevantRegions.find(r => r._id === activeRegionId)?.name ?? '' })}
                                  </span>
                                  {multi && (
                                    <button
                                      type="button"
                                      onClick={() => handleRegionClick(activeRegionId!, close)}
                                      className="text-[10px] font-bold uppercase tracking-wider text-lime-500 hover:text-lime-400"
                                    >
                                      {locationsByRegion.get(activeRegionId)?.every(l => selectedLocationIds.includes(l._id))
                                        ? t('clear')
                                        : t('selectAll')}
                                    </button>
                                  )}
                                </div>
                                <div className="px-2">
                                  {locationsByRegion.get(activeRegionId)?.map((loc) => (
                                    <button
                                      type="button"
                                      key={loc._id}
                                      onClick={() => handleLocationToggle(loc._id, activeRegionId!, close)}
                                      className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors rounded-lg hover:bg-surface-hover ${selectedLocationIds.includes(loc._id) ? 'text-lime-400 font-medium bg-lime-500/10' : 'text-foreground'}`}
                                    >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-base flex-shrink-0">{getFlagEmoji(loc.country || loc.code)}</span>
                                        <span className="truncate">{loc.name}</span>
                                      </div>
                                      {selectedLocationIds.includes(loc._id) && <Check className="w-4 h-4" />}
                                    </button>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <div className="h-full flex flex-col items-center justify-center text-muted-foreground px-6 text-center">
                                <MapPin className="w-8 h-8 mb-3 opacity-20" />
                                <p className="text-sm">{t('hoverRegionHint')}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Flat Grouped List (used at all sizes) */}
                        <div className="flex-1 overflow-y-auto py-2">
                          {multi && (
                            <button
                              type="button"
                              onClick={() => handleSelectAll(close)}
                              className={`w-full flex items-center justify-between px-4 py-3 text-sm transition-colors hover:bg-surface-hover mb-2 ${isAllSelected ? 'text-lime-400 font-medium bg-lime-500/5' : 'text-foreground'}`}
                            >
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4" />
                                <span>{t('allLocations')}</span>
                              </div>
                              {isAllSelected && <Check className="w-4 h-4" />}
                            </button>
                          )}

                          {relevantRegions.map((region) => (
                            <div key={region._id} className="mb-4">
                              <div className="px-4 py-2 flex items-center justify-between bg-surface-hover/30">
                                <span className={`text-[10px] font-bold uppercase tracking-wider opacity-60 truncate mr-2 ${(regionSelectionStats.get(region._id)?.selected ?? 0) > 0 ? 'text-lime-500' : 'text-muted-foreground'}`}>
                                  {region.name}
                                  {(() => {
                                    const stats = regionSelectionStats.get(region._id);
                                    if (!stats || stats.selected === 0) return null;
                                    return ` (${stats.selected === stats.total ? t('all') : `${stats.selected}/${stats.total}`})`;
                                  })()}
                                </span>
                                <div className="flex items-center gap-3">
                                  {(regionSelectionStats.get(region._id)?.selected ?? 0) > 0 && (
                                    <Check className="w-3.5 h-3.5 text-lime-500" />
                                  )}
                                  {multi && (
                                    <button
                                      type="button"
                                      onClick={() => handleRegionClick(region._id, close)}
                                      className="text-[10px] font-bold uppercase tracking-wider text-lime-500 hover:text-lime-400 flex-shrink-0"
                                    >
                                      {locationsByRegion.get(region._id)?.every(l => selectedLocationIds.includes(l._id))
                                        ? t('clear')
                                        : t('selectAll')}
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="px-2 mt-1">
                                {locationsByRegion.get(region._id)?.map((loc) => (
                                  <button
                                    type="button"
                                    key={loc._id}
                                    onClick={() => handleLocationToggle(loc._id, region._id, close)}
                                    className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors rounded-lg hover:bg-surface-hover ${selectedLocationIds.includes(loc._id) ? 'text-lime-400 font-medium bg-lime-500/10' : 'text-foreground'}`}
                                  >
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-base flex-shrink-0">{getFlagEmoji(loc.country || loc.code)}</span>
                                        <span className="truncate">{loc.name}</span>
                                      </div>
                                    {selectedLocationIds.includes(loc._id) && <Check className="w-4 h-4" />}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
}
